import { IEventBus } from 'components/eventBus/IEventBus';
import { ECommonEvent, TCommonEventPayload } from 'types/common';
import { bind } from 'utils/bind';
import { simpleLog } from 'utils/simpleLog';

import { ILogEventPayload, TLogLevel } from './ILogger';
import { Logger } from './Logger';
import { ELoggerEvents, TLoggerEvents } from './Logger.events';

type TEventType = string;

const logLevelToMsg: Record<TLogLevel, (msg: string, payload?: Record<string, any>) => void> = {
  debug: (msg, payload) => simpleLog(`%c${msg}`, 'color: rgb(102, 102, 102)', payload),
  verbose: (msg, payload) => simpleLog(`%c${msg}`, 'color: rgb(36, 114, 200)', payload),
  info: (msg, payload) => simpleLog(`%c${msg}`, 'color: rgb(13, 188, 121)', payload),
  warn: (msg, payload) => simpleLog(`%c${msg}`, 'color: rgb(229, 229, 16)', payload),
  error: (msg, payload) => simpleLog(`%c${msg}`, 'color: rgb(241, 76, 76)', payload),
  fatal: (msg, payload) =>
    simpleLog(`%c${msg}`, 'background: rgb(241, 76, 76); color: #fff', payload),
};

const defaultLevels: TLogLevel[] = [];

export class LoggerConsoleTransport {
  private _enableErrorAutoLog: boolean;
  private readonly _levels: TLogLevel[];
  private readonly _onLogMessageReceivedToken: string;
  private readonly _onErrorReceivedToken: string;
  private readonly _ignoreDomains: string[];
  private readonly _additionalScopeSubscriptions: string[];
  private readonly _eventBus: IEventBus<TLoggerEvents>;
  private readonly _appId: string;

  constructor(
    eventBus: IEventBus<TLoggerEvents>,
    levels: TLogLevel[] = defaultLevels,
    additionalScopes: string[] = [],
    ignoreDomains: string[] = [],
    enableErrorAutoLog: boolean = true,
  ) {
    this._levels = levels;
    this._eventBus = eventBus;
    this._enableErrorAutoLog = enableErrorAutoLog;
    this._onLogMessageReceivedToken = this._eventBus.subscribe(
      ELoggerEvents.LOG,
      this._onMessageReceived,
    );
    this._onErrorReceivedToken = this._eventBus.subscribe(
      ECommonEvent.COMMON_ERROR,
      this._onErrorReceived,
    );
    this._ignoreDomains = ignoreDomains;

    this._additionalScopeSubscriptions = additionalScopes.map((additionalScope) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line @streaming/eventbus/noUnsavedSubscribers
      return this._eventBus.subscribe(additionalScope as unknown as TEventType, (payload) => {
        this._onAdditionalScopeMessageReceived(additionalScope, payload as never);
      });
    });
    this._appId = `${(Math.random() + 1).toString(36).substring(2, 6)}`;
  }

  public destroy(): void {
    this._eventBus.unsubscribe(this._onLogMessageReceivedToken);
    this._eventBus.unsubscribe(this._onErrorReceivedToken);

    this._additionalScopeSubscriptions.forEach(this._eventBus.unsubscribe);
  }

  @bind
  private _onMessageReceived(data: ILogEventPayload): void {
    const { domain, timestamp, level, message, payload } = data;

    if (!this._levels.includes(level) || this._ignoreDomains.includes(domain)) {
      return;
    }

    const levelLogger: (msg: string, payload?: Record<string, any>) => void =
      logLevelToMsg[level] || logLevelToMsg['info'];

    levelLogger(
      `(${this._appId}) :: [${domain}] ${level.toUpperCase()}:\n${message}@${timestamp}`,
      payload,
    );
  }

  @bind
  private _onAdditionalScopeMessageReceived(scope: string, payload: never): void {
    const levelLogger: (msg: string) => void = logLevelToMsg['verbose'];

    levelLogger(`[${scope}] Event receiver with payload ${payload} @ ${performance.now()}`);
  }

  @bind
  private _onErrorReceived({ error }: TCommonEventPayload<ECommonEvent.COMMON_ERROR>): void {
    if (this._enableErrorAutoLog) {
      Logger(this._eventBus, 'COMMON ERROR').error(
        `Error in ${error.domain}: ${error.message} ${error.info && ' (' + error.info + ')'}`,
      );
    }
  }
}
